import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Grid,
  Alert,
  AlertTitle,
  IconButton,
} from '@mui/material';
import {
  DesktopWindowsOutlined,
  CheckCircleOutlined,
  PersonOutlined,
  CableOutlined,
  RefreshOutlined,
  LinkOffOutlined,
  VisibilityOutlined,
  FiberManualRecord,
  WarningAmberOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import PulsingDot from '../../components/common/PulsingDot';

// ---------- Types ----------

interface DesktopAssignment {
  studentId: string;
  studentName: string;
  sessionId: string;
  sessionName: string;
}

interface ActiveConnection {
  connectionId: string;
  startedAt: string | null;
  remoteHost: string;
}

interface Desktop {
  id: string;
  name: string;
  protocol: string;
  status: 'available' | 'assigned' | 'connected' | 'error';
  assignedTo: DesktopAssignment | null;
  activeConnection: ActiveConnection | null;
}

interface DesktopPoolData {
  summary: {
    totalDesktops: number;
    assignedDesktops: number;
    availableDesktops: number;
    activeConnections: number;
  };
  desktops: Desktop[];
  guacamoleReachable: boolean;
}

interface ConnectionDetail {
  connectionId: string;
  desktopName: string;
  username: string;
  startDate: string | null;
  remoteHost: string;
  student: {
    id: string;
    name: string;
    email: string;
    sessionName: string;
  } | null;
}

interface HealthData {
  status: 'healthy' | 'unhealthy' | 'unreachable';
  responseTimeMs: number;
  guacamoleUrl: string;
  version: string | null;
  activeConnectionCount: number;
}

// ---------- Constants ----------

const statusColorMap: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  available: 'success',
  assigned: 'info',
  connected: 'warning',
  error: 'error',
};

// ---------- Component ----------

const AdminDesktops: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // State
  const [pool, setPool] = useState<DesktopPoolData | null>(null);
  const [connections, setConnections] = useState<ConnectionDetail[]>([]);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Disconnect dialog
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<{
    connectionId: string;
    studentName: string;
    desktopName: string;
  } | null>(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---------- Fetchers ----------

  const fetchAll = useCallback(async () => {
    try {
      const [poolRes, connRes] = await Promise.all([
        adminService.getDesktops(),
        adminService.getDesktopConnections(),
      ]);
      setPool(poolRes.data);
      setConnections(connRes.data.connections || []);
    } catch {
      showNotification('Failed to load desktop pool', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await adminService.getDesktopHealth();
      setHealth(res.data);
    } catch {
      showNotification('Failed to check Guacamole health', 'error');
      setHealth({ status: 'unreachable', responseTimeMs: 0, guacamoleUrl: '', version: null, activeConnectionCount: 0 });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  // ---------- Effects ----------

  useEffect(() => {
    fetchAll();
    fetchHealth();
  }, [fetchAll, fetchHealth]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchAll, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchAll]);

  // ---------- Handlers ----------

  const openDisconnect = (connectionId: string, studentName: string, desktopName: string) => {
    setDisconnectTarget({ connectionId, studentName, desktopName });
    setDisconnectOpen(true);
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnectLoading(true);
    try {
      await adminService.killDesktopConnection(disconnectTarget.connectionId);
      setDisconnectOpen(false);
      fetchAll();
    } catch {
      showNotification('Failed to disconnect desktop', 'error');
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchAll();
    fetchHealth();
  };

  // ---------- Computed ----------

  const summary = pool?.summary || { totalDesktops: 0, assignedDesktops: 0, availableDesktops: 0, activeConnections: 0 };
  const desktops = pool?.desktops || [];
  const guacamoleReachable = pool?.guacamoleReachable ?? false;

  // ---------- Render ----------

  if (loading && !pool) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Desktop Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Auto-refresh 30s</Typography>}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshOutlined sx={{ fontSize: 18 }} />}
            onClick={handleRefresh}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }}>
            <DesktopWindowsOutlined sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{summary.totalDesktops}</Typography>
            <Typography variant="body2" color="text.secondary">Total Desktops</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }}>
            <CheckCircleOutlined sx={{ fontSize: 32, color: '#22c55e', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#22c55e' }}>{summary.availableDesktops}</Typography>
            <Typography variant="body2" color="text.secondary">Available</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }}>
            <PersonOutlined sx={{ fontSize: 32, color: '#2196f3', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>{summary.assignedDesktops}</Typography>
            <Typography variant="body2" color="text.secondary">Assigned</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }}>
            <CableOutlined sx={{ fontSize: 32, color: '#ed6c02', mb: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>{summary.activeConnections}</Typography>
              {summary.activeConnections > 0 && <PulsingDot size={10} />}
            </Box>
            <Typography variant="body2" color="text.secondary">Active Connections</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Guacamole Health Indicator */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FiberManualRecord
            sx={{
              fontSize: 14,
              color: !guacamoleReachable || health?.status === 'unreachable'
                ? '#ef4444'
                : health?.status === 'healthy'
                  ? '#22c55e'
                  : '#ed6c02',
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Guacamole:{' '}
            {!guacamoleReachable || health?.status === 'unreachable'
              ? 'Unreachable'
              : health?.status === 'healthy'
                ? `Healthy (${health.responseTimeMs}ms)`
                : 'Unhealthy'}
          </Typography>
          {health?.guacamoleUrl && (
            <Typography variant="caption" color="text.secondary">
              {health.guacamoleUrl}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          onClick={fetchHealth}
          disabled={healthLoading}
          startIcon={healthLoading ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
        >
          Check
        </Button>
      </Paper>

      {/* Guacamole Unreachable Alert */}
      {!guacamoleReachable && (
        <Alert severity="error" icon={<WarningAmberOutlined />} sx={{ mb: 2.5, borderRadius: '12px' }}>
          <AlertTitle>Guacamole Server Unreachable</AlertTitle>
          <Typography variant="body2">
            Cannot connect to the Guacamole server{health?.guacamoleUrl ? ` at ${health.guacamoleUrl}` : ''}.
            Check that Guacamole is running, verify network connectivity and API credentials.
          </Typography>
        </Alert>
      )}

      {/* Desktop Pool Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden', mb: 2.5 }} elevation={0}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Desktop Pool
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Protocol</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Session</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Connected Since</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {desktops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <DesktopWindowsOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {guacamoleReachable
                        ? 'No desktops configured. Check Guacamole server configuration.'
                        : 'Unable to load desktops — Guacamole is unreachable.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                desktops.map((d) => (
                  <TableRow key={d.id} hover sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {d.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 500 }}>
                        {d.protocol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                        size="small"
                        color={statusColorMap[d.status] || 'default'}
                        icon={d.status === 'connected' ? <FiberManualRecord sx={{ fontSize: '10px !important' }} /> : undefined}
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {d.assignedTo?.studentName || '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {d.assignedTo ? (
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`/admin/sessions/${d.assignedTo!.sessionId}`)}
                        >
                          {d.assignedTo.sessionName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{'\u2014'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {d.activeConnection?.startedAt ? (
                        <Tooltip title={dayjs(d.activeConnection.startedAt).format('MMM D, YYYY h:mm:ss A')}>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(d.activeConnection.startedAt).fromNow()}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{'\u2014'}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {d.status === 'connected' && d.activeConnection && (
                        <Tooltip title="Force Disconnect">
                          <IconButton
                            size="small"
                            sx={{ color: 'error.main' }}
                            onClick={() =>
                              openDisconnect(
                                d.activeConnection!.connectionId,
                                d.assignedTo?.studentName || 'Unknown',
                                d.name,
                              )
                            }
                          >
                            <LinkOffOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {d.assignedTo && (
                        <Tooltip title="View Session">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/sessions/${d.assignedTo!.sessionId}`)}
                          >
                            <VisibilityOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Active Connections Panel */}
      <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Active Connections
          </Typography>
          <Chip label={connections.length} size="small" color="warning" sx={{ fontWeight: 600, height: 22 }} />
        </Box>

        {connections.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CableOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No active desktop connections</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              {connections.map((c) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.connectionId}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      borderLeft: '3px solid #ed6c02',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {c.desktopName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {c.student?.name || c.username || 'Unknown'}
                        </Typography>
                      </Box>
                      <PulsingDot size={8} />
                    </Box>

                    {c.student?.email && (
                      <Typography variant="caption" color="text.secondary">
                        {c.student.email}
                      </Typography>
                    )}

                    {c.student?.sessionName && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => {
                          // We don't have sessionId in connections response directly,
                          // but we can navigate to sessions page
                        }}
                      >
                        Session: {c.student.sessionName}
                      </Typography>
                    )}

                    {c.startDate && (
                      <Tooltip title={dayjs(c.startDate).format('MMM D, YYYY h:mm:ss A')}>
                        <Typography variant="caption" color="text.secondary">
                          Connected {dayjs(c.startDate).fromNow()}
                        </Typography>
                      </Tooltip>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOffOutlined sx={{ fontSize: 16 }} />}
                      onClick={() =>
                        openDisconnect(
                          c.connectionId,
                          c.student?.name || c.username || 'Unknown',
                          c.desktopName,
                        )
                      }
                      sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Force Disconnect
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Force Disconnect Dialog */}
      <Dialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Disconnect {disconnectTarget?.studentName} from {disconnectTarget?.desktopName}?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            The student will lose their current desktop session. They can reconnect if the tech eval session is still active.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDisconnectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnectLoading}
          >
            {disconnectLoading ? <CircularProgress size={20} /> : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDesktops;
